/**
 * Inbound Message Logger
 *
 * Efficiently logs incoming WhatsApp messages to Supabase database
 * using async non-blocking approach with batch processing to prevent high load.
 *
 * Features:
 * - Async/non-blocking (doesn't delay message processing)
 * - Batch insert (multiple messages in single query)
 * - Error isolation (DB errors don't affect message handling)
 * - Automatic retry on failure
 * - Memory-efficient (buffer size limited)
 */

import { query } from "../db.js";
import pino from "pino";
import { downloadAndSaveMedia, ensureMediaDir } from "./mediaDownloader.js";

// Columns inserted into whatsapp_inbound_messages — order matches `record` shape below.
const INBOUND_COLUMNS = [
    "session_id",
    "session_device_id",
    "message_id",
    "from_number",
    "real_sender_number",
    "sender_name",
    "message",
    "message_type",
    "media_url",
    "media_filename",
    "media_size",
    "media_mimetype",
    "quoted_message_id",
    "mentions",
    "metadata",
    "received_at",
    "created_at",
    "updated_at",
];

// JSON columns must be stringified before passing to pg.
const JSON_COLUMNS = new Set(["mentions", "metadata"]);

const buildBatchInsert = (records) => {
    const cols = INBOUND_COLUMNS.join(", ");
    const values = [];
    const placeholders = records
        .map((record) => {
            const rowPlaceholders = INBOUND_COLUMNS.map((col) => {
                let v = record[col];
                if (v !== null && v !== undefined && JSON_COLUMNS.has(col)) {
                    v = JSON.stringify(v);
                }
                values.push(v ?? null);
                return `$${values.length}`;
            });
            return `(${rowPlaceholders.join(", ")})`;
        })
        .join(", ");
    return {
        text: `INSERT INTO whatsapp_inbound_messages (${cols}) VALUES ${placeholders} RETURNING *`,
        values,
    };
};

const logger = pino({ level: "warn" });

// Ensure media directory exists on startup
ensureMediaDir().catch((err) => {
    logger.error({ error: err.message }, "Failed to ensure media directory");
});

// ============================================
// CONFIGURATION
// ============================================

const BATCH_SIZE = 10; // Insert in batches of 10 messages
const BATCH_TIMEOUT = 5000; // Max 5 seconds wait before flushing batch
const MAX_RETRIES = 3; // Retry failed inserts up to 3 times
const RETRY_DELAY = 1000; // 1 second delay between retries

// ============================================
// MESSAGE BUFFER
// ============================================

let messageBuffer = [];
let flushTimeout = null;
let isProcessing = false;

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Extract message type from Baileys message object
 * @param {Object} message - Baileys message object
 * @returns {string} Message type (text, image, audio, video, document)
 */
const extractMessageType = (message) => {
    if (!message) return "text";

    if (message.imageMessage) return "image";
    if (message.audioMessage) return "audio";
    if (message.videoMessage) return "video";
    if (message.documentMessage) return "document";
    if (message.stickerMessage) return "sticker";
    if (message.locationMessage) return "location";
    if (message.contactMessage) return "contact";
    if (message.extendedTextMessage) return "text";

    return "text";
};

/**
 * Extract media information from message
 * @param {Object} message - Baileys message object
 * @param {string} messageType - Type of message
 * @returns {Object} Media info { url, filename, size, mimetype }
 */
const extractMediaInfo = (message, messageType) => {
    const mediaInfo = {
        url: null,
        filename: null,
        size: null,
        mimetype: null,
    };

    try {
        let mediaMessage = null;

        switch (messageType) {
            case "image":
                mediaMessage = message.imageMessage;
                mediaInfo.mimetype = mediaMessage?.mimetype || "image/jpeg";
                break;
            case "audio":
                mediaMessage = message.audioMessage;
                mediaInfo.mimetype = mediaMessage?.mimetype || "audio/mpeg";
                break;
            case "video":
                mediaMessage = message.videoMessage;
                mediaInfo.mimetype = mediaMessage?.mimetype || "video/mp4";
                break;
            case "document":
                mediaMessage = message.documentMessage;
                mediaInfo.mimetype = mediaMessage?.mimetype || "application/pdf";
                mediaInfo.filename = mediaMessage?.fileName || null;
                break;
        }

        if (mediaMessage) {
            // Note: Direct media URL is not provided by Baileys
            // You would need to download and upload to your storage
            // For now, we store the info that media was present
            mediaInfo.size = mediaMessage?.fileLength || null;
        }
    } catch (error) {
        logger.warn({ error: error.message }, "Error extracting media info");
    }

    return mediaInfo;
};

/**
 * Extract quoted message ID if present
 * @param {Object} message - Baileys message object
 * @returns {string|null} Quoted message ID
 */
const extractQuotedMessageId = (message) => {
    try {
        if (message?.extendedTextMessage?.contextInfo?.stanzaId) {
            return message.extendedTextMessage.contextInfo.stanzaId;
        }
        if (message?.imageMessage?.contextInfo?.stanzaId) {
            return message.imageMessage.contextInfo.stanzaId;
        }
        if (message?.videoMessage?.contextInfo?.stanzaId) {
            return message.videoMessage.contextInfo.stanzaId;
        }
    } catch (error) {
        logger.warn({ error: error.message }, "Error extracting quoted message ID");
    }
    return null;
};

/**
 * Extract mentions from message
 * @param {Object} message - Baileys message object
 * @returns {Array|null} Array of mentioned JIDs
 */
const extractMentions = (message) => {
    try {
        // Check for mentions in different message types
        const contextInfo =
            message?.extendedTextMessage?.contextInfo ||
            message?.imageMessage?.contextInfo ||
            message?.videoMessage?.contextInfo;

        if (contextInfo?.mentionedJid) {
            return contextInfo.mentionedJid;
        }
    } catch (error) {
        logger.warn({ error: error.message }, "Error extracting mentions");
    }
    return null;
};

/**
 * Get real sender number from message
 * Uses remoteJidAlt (real GSM number) as priority, fallback to remoteJid
 * @param {Object} messageKey - Baileys message.key object
 * @returns {string} Real sender number in format 62xxx@s.whatsapp.net
 */
const getRealSenderNumber = (messageKey) => {
    if (!messageKey) return null;

    // Priority: remoteJidAlt (contains real GSM number like 62xxx@s.whatsapp.net)
    if (messageKey.remoteJidAlt) {
        return messageKey.remoteJidAlt;
    }

    // Fallback: remoteJid (may contain @lid, @newsletter, etc)
    let jid = messageKey.remoteJid;
    if (!jid) return null;

    // Convert @lid, @newsletter, @broadcast to @s.whatsapp.net
    const convertibleSuffixes = ["@lid", "@newsletter", "@broadcast"];
    for (const suffix of convertibleSuffixes) {
        if (jid.endsWith(suffix)) {
            return jid.replace(suffix, "@s.whatsapp.net");
        }
    }

    return jid;
};

/**
 * Get sender name from message
 * @param {Object} message - Baileys message object
 * @param {string} userId - User JID
 * @returns {string|null} Sender name
 */
const getSenderName = (message, userId) => {
    try {
        // Try to get name from pushName
        if (message?.pushName) {
            return message.pushName;
        }

        // Try to get from notification
        if (message?.notificationType) {
            return userId.split("@")[0]; // Fallback to phone number
        }
    } catch (error) {
        logger.warn({ error: error.message }, "Error getting sender name");
    }
    return null;
};

/**
 * Check if message is from personal chat (not group/newsletter/broadcast)
 * @param {string} jid - JID to check
 * @returns {boolean} True if personal chat
 */
const isPersonalMessage = (jid) => {
    if (!jid) return false;

    // Exclude groups, newsletters, broadcasts, status
    const excludedSuffixes = ["@g.us", "@newsletter", "@broadcast", "status@broadcast"];

    for (const suffix of excludedSuffixes) {
        if (jid.endsWith(suffix) || jid === suffix) {
            return false;
        }
    }

    return true;
};

/**
 * Check if message has media (image, video, audio, document, sticker)
 * @param {Object} message - Baileys message object
 * @returns {boolean} True if message has media
 */
const hasMedia = (message) => {
    const mediaTypes = [
        "imageMessage",
        "videoMessage",
        "audioMessage",
        "voiceMessage",
        "documentMessage",
        "stickerMessage",
    ];

    for (const type of mediaTypes) {
        if (message[type]) {
            return true;
        }
    }

    return false;
};

/**
 * Format metadata JSON for storage
 * @param {Object} message - Baileys message object
 * @param {string} messageType - Type of message
 * @returns {Object} Formatted metadata
 */
const formatMetadata = (message, messageType) => {
    const metadata = {
        messageType,
        timestamp: message?.messageTimestamp || Date.now(),
        pushName: message?.pushName || null,
        hasMedia: ["image", "audio", "video", "document"].includes(messageType),
    };

    // Add type-specific metadata
    if (messageType === "image") {
        metadata.width = message?.imageMessage?.width || null;
        metadata.height = message?.imageMessage?.height || null;
    } else if (messageType === "location") {
        metadata.latitude = message?.locationMessage?.degreesLatitude || null;
        metadata.longitude = message?.locationMessage?.degreesLongitude || null;
    }

    return metadata;
};

// ============================================
// BATCH PROCESSING
// ============================================

/**
 * Flush message buffer to database
 * @param {boolean} force - Force flush even if buffer not full
 */
const flushBuffer = async (force = false) => {
    // Don't flush if already processing or buffer empty
    if (isProcessing || messageBuffer.length === 0) {
        return;
    }

    // Don't flush unless buffer is full or force flush
    if (!force && messageBuffer.length < BATCH_SIZE) {
        return;
    }

    // Clear timeout if exists
    if (flushTimeout) {
        clearTimeout(flushTimeout);
        flushTimeout = null;
    }

    // Get messages to insert and clear buffer
    const messagesToInsert = [...messageBuffer];
    messageBuffer = [];
    isProcessing = true;

    try {
        logger.debug(
            { count: messagesToInsert.length },
            "Flushing inbound messages to database"
        );

        const { text, values } = buildBatchInsert(messagesToInsert);

        let lastError = null;
        let inserted = false;

        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
            try {
                await query(text, values);
                if (attempt === 0) {
                    logger.info(
                        { count: messagesToInsert.length },
                        "Successfully inserted inbound messages"
                    );
                } else {
                    logger.info(
                        { count: messagesToInsert.length, retry: attempt },
                        "Successfully inserted inbound messages after retry"
                    );
                }
                inserted = true;
                break;
            } catch (err) {
                lastError = err;
                if (attempt < MAX_RETRIES) {
                    logger.warn(
                        {
                            error: err.message,
                            retry: attempt + 1,
                            maxRetries: MAX_RETRIES,
                        },
                        "Retrying failed insert"
                    );
                    await new Promise((resolve) =>
                        setTimeout(resolve, RETRY_DELAY * (attempt + 1))
                    );
                }
            }
        }

        if (!inserted) {
            logger.error(
                {
                    error: lastError?.message,
                    count: messagesToInsert.length,
                    messageIds: messagesToInsert.map((m) => m.message_id),
                },
                "Failed to insert inbound messages after all retries"
            );
        }
    } catch (error) {
        logger.error(
            { error: error.message, stack: error.stack },
            "Unexpected error flushing inbound messages"
        );
    } finally {
        isProcessing = false;
    }
};

/**
 * Schedule buffer flush
 */
const scheduleFlush = () => {
    if (flushTimeout) {
        return; // Already scheduled
    }

    flushTimeout = setTimeout(() => {
        flushBuffer(true); // Force flush after timeout
    }, BATCH_TIMEOUT);
};

// ============================================
// MAIN EXPORT FUNCTION
// ============================================

/**
 * Log inbound message to database (async, non-blocking)
 *
 * This function is non-blocking and will not delay message processing.
 * Messages are buffered and inserted in batches for efficiency.
 *
 * @param {Object} params - Message parameters
 * @param {string} params.sessionId - Session ID
 * @param {string} params.sessionDeviceId - Device ID
 * @param {Object} params.message - Baileys message object
 * @param {string} params.plainText - Extracted text message
 */
const logInboundMessage = async ({
    sessionId,
    sessionDeviceId,
    message,
    plainText,
}) => {
    try {
        // Extract message data
        const messageId = message?.key?.id;
        if (!messageId) {
            logger.warn("No message ID found, skipping log");
            return;
        }

        // from_number: original format (including @g.us, @newsletter, @lid, etc)
        const fromNumber = message?.key?.remoteJid;
        if (!fromNumber) {
            logger.warn("No from number found, skipping log");
            return;
        }

        // Skip system messages (e.g., "[System message: 5]", encryption updates, etc)
        if (plainText && plainText.startsWith("[System message:")) {
            logger.debug({ messageId, plainText }, "Skipping system message");
            return;
        }

        // real_sender_number: real GSM number (62xxx@s.whatsapp.net) from remoteJidAlt
        const realSenderNumber = getRealSenderNumber(message?.key);

        const messageType = extractMessageType(message);
        const mediaInfo = extractMediaInfo(message, messageType);
        const quotedMessageId = extractQuotedMessageId(message);
        const mentions = extractMentions(message);
        const senderName = getSenderName(message, fromNumber);
        const metadata = formatMetadata(message, messageType);

        // Prepare database record
        const record = {
            session_id: sessionId,
            session_device_id: sessionDeviceId,
            message_id: messageId,
            from_number: fromNumber,
            real_sender_number: realSenderNumber,
            sender_name: senderName,
            message: plainText || "",
            message_type: messageType,
            media_url: mediaInfo.url,
            media_filename: mediaInfo.filename,
            media_size: mediaInfo.size,
            media_mimetype: mediaInfo.mimetype,
            quoted_message_id: quotedMessageId,
            mentions: mentions,
            metadata: metadata,
            received_at: new Date(
                (message?.messageTimestamp || Date.now()) * 1000
            ).toISOString(),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        };

        // For personal messages with media: download first, then insert with media_url
        // This avoids race condition where update happens before insert
        const shouldDownloadMedia = isPersonalMessage(fromNumber) && hasMedia(message.message);

        if (shouldDownloadMedia) {
            // Download media synchronously (with timeout protection inside downloadAndSaveMedia)
            const mediaResult = await downloadAndSaveMedia(message.message);

            if (mediaResult && mediaResult.accessibleUrl) {
                // Update record with media info before inserting
                record.media_url = mediaResult.accessibleUrl;
                record.media_filename = mediaResult.filename;
                record.media_size = mediaResult.size;
                record.media_mimetype = mediaResult.mimetype;

                logger.warn(
                    { messageId, filename: mediaResult.filename, url: mediaResult.accessibleUrl },
                    "Media downloaded before insert - record updated with media_url"
                );
            } else {
                logger.warn(
                    { messageId, error: mediaResult?.error },
                    "Media download failed - inserting without media_url"
                );
            }
        }

        // Add to buffer (non-blocking)
        messageBuffer.push(record);

        // Schedule flush if buffer is getting full
        if (messageBuffer.length >= BATCH_SIZE) {
            // Flush immediately without awaiting (fire and forget)
            flushBuffer().catch((err) => {
                logger.error({ error: err.message }, "Error in flushBuffer");
            });
        } else {
            // Schedule delayed flush
            scheduleFlush();
        }

        logger.debug(
            {
                messageId,
                fromNumber,
                messageType,
                bufferSize: messageBuffer.length,
            },
            "Inbound message added to buffer"
        );
    } catch (error) {
        // Don't throw - error logging should never crash the message handler
        logger.error(
            { error: error.message, stack: error.stack },
            "Error logging inbound message"
        );
    }
};

/**
 * Force flush all buffered messages immediately
 * Useful for graceful shutdown
 */
const forceFlush = async () => {
    if (messageBuffer.length > 0) {
        logger.info(
            { count: messageBuffer.length },
            "Force flushing inbound message buffer"
        );
        await flushBuffer(true);
    }
};

/**
 * Get current buffer size (for monitoring)
 */
const getBufferSize = () => {
    return messageBuffer.length;
};

// ============================================
// EXPORTS
// ============================================

export { logInboundMessage, forceFlush, getBufferSize };
